import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink, X } from "lucide-react";

import { SearchableMultiSelect } from "@/components/ui/SearchableMultiSelect";
import type { SearchableSelectOption } from "@/components/ui/SearchableSelect";
import { freshdeskTicketUrl } from "@/lib/freshdeskAgentUrl";

import {
  fetchQualityFreshdeskTicketQuickSearch,
  type QualityFreshdeskTicketSearchRow,
} from "./rmaApi";

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [delayMs, value]);
  return debounced;
}

function looksLikeFreshdeskTicketSearch(query: string): boolean {
  const trimmed = query.trim();
  if (trimmed.length >= 2) {
    return true;
  }
  return /^\d+$/.test(trimmed);
}

function ticketLabel(row: Pick<QualityFreshdeskTicketSearchRow, "id" | "subject">): string {
  const subject = row.subject?.trim();
  if (subject != null && subject !== "") {
    return `#${row.id} ${subject}`;
  }
  return `Ticket ${row.id}`;
}

function ticketDescription(row: QualityFreshdeskTicketSearchRow): string {
  return [row.requesterName?.trim(), row.requesterEmail?.trim()]
    .filter((part): part is string => part != null && part !== "")
    .join(" · ");
}

function toSearchOption(row: QualityFreshdeskTicketSearchRow): SearchableSelectOption {
  return {
    value: String(row.id),
    label: ticketLabel(row),
    description: ticketDescription(row) || null,
  };
}

export function QualityFreshdeskTicketPicker({
  id,
  values,
  knownLabels,
  disabled,
  onChange,
}: {
  id: string;
  values: number[];
  knownLabels?: Record<number, string | null | undefined>;
  disabled?: boolean;
  onChange: (ticketIds: number[]) => void;
}): JSX.Element {
  const [searchValue, setSearchValue] = useState("");
  const [knownRows, setKnownRows] = useState<Map<number, QualityFreshdeskTicketSearchRow>>(new Map());
  const debouncedSearch = useDebouncedValue(searchValue.trim(), 250);
  const lookupQuery = values[0] != null ? String(values[0]) : "";
  const searchQuery = looksLikeFreshdeskTicketSearch(debouncedSearch) ? debouncedSearch : lookupQuery;

  const searchResultsQuery = useQuery({
    queryKey: ["quality", "freshdesk-ticket-quick-search", searchQuery],
    queryFn: () => fetchQualityFreshdeskTicketQuickSearch(searchQuery),
    enabled: looksLikeFreshdeskTicketSearch(searchQuery),
    staleTime: 20_000,
  });

  useEffect(() => {
    const rows = searchResultsQuery.data?.rows ?? [];
    if (rows.length === 0) {
      return;
    }
    setKnownRows((current) => {
      const next = new Map(current);
      for (const row of rows) {
        next.set(row.id, row);
      }
      return next;
    });
  }, [searchResultsQuery.data?.rows]);

  const selectedRows = values.map((ticketId) => ({
    id: ticketId,
    chipLabel:
      knownRows.get(ticketId) != null
        ? ticketLabel(knownRows.get(ticketId)!)
        : knownLabels?.[ticketId]?.trim() || `Ticket ${ticketId}`,
  }));

  const options = useMemo(() => {
    const rows = searchResultsQuery.data?.rows ?? [];
    const byId = new Map<number, QualityFreshdeskTicketSearchRow>();
    for (const row of rows) {
      byId.set(row.id, row);
    }
    for (const ticketId of values) {
      const selected = knownRows.get(ticketId);
      if (selected != null) {
        byId.set(selected.id, selected);
      }
    }
    const fromRows = [...byId.values()].map(toSearchOption);
    const present = new Set(fromRows.map((option) => option.value));
    for (const selected of selectedRows) {
      if (!present.has(String(selected.id))) {
        fromRows.push({
          value: String(selected.id),
          label: selected.chipLabel,
        });
      }
    }
    return fromRows;
  }, [searchResultsQuery.data?.rows, knownRows, values, selectedRows]);

  return (
    <SearchableMultiSelect
      id={id}
      variant="chips"
      alwaysShowChips
      selectedValues={new Set(values.map(String))}
      onSelectedValuesChange={(next) => {
        const ids = [...next]
          .map((value) => Number(value))
          .filter((parsed) => Number.isFinite(parsed) && parsed >= 1);
        onChange(ids);
        setSearchValue("");
      }}
      options={options}
      searchValue={searchValue}
      onSearchChange={setSearchValue}
      placeholder="Search ticket number, subject, URL, or requester"
      searchPlaceholder="Search ticket number, subject, URL, or requester"
      emptyMessage={
        searchResultsQuery.isError
          ? "Could not search Freshdesk."
          : searchValue.trim().length < 1
            ? "Type a ticket number, subject, Freshdesk URL, or requester."
            : "No Freshdesk tickets match."
      }
      isLoading={searchResultsQuery.isFetching}
      disabled={disabled}
      panelMinWidth={360}
      renderChip={({ option, disabled: chipDisabled, onRemove }) => {
        const ticketId = Number(option.value);
        const selected = selectedRows.find((row) => row.id === ticketId);
        return (
          <span className="inline-flex max-w-full items-center gap-1 rounded-md bg-slate-100 px-2 py-0.5 text-xs text-slate-800">
            <span className="truncate">{selected?.chipLabel ?? option.label}</span>
            <a
              href={freshdeskTicketUrl(ticketId)}
              target="_blank"
              rel="noreferrer noopener"
              className="shrink-0 text-slate-400 hover:text-brand-600"
              aria-label={`Open ${selected?.chipLabel ?? option.label} in Freshdesk`}
              onClick={(event) => event.stopPropagation()}
            >
              <ExternalLink className="h-3.5 w-3.5" aria-hidden />
            </a>
            <button
              type="button"
              className="rounded p-0.5 text-slate-500 hover:bg-slate-200 hover:text-slate-800 disabled:pointer-events-none"
              aria-label={`Remove ${selected?.chipLabel ?? option.label}`}
              disabled={chipDisabled}
              onClick={(event) => {
                event.stopPropagation();
                onRemove();
              }}
            >
              <X className="h-3 w-3" aria-hidden />
            </button>
          </span>
        );
      }}
    />
  );
}
